/* LEGAL-NOTICE
   © Copyright 2018 - Quid Informatica S.p.A. - All Rights Reserved
   https://quidinfo.it/qinetic/licence.html - "Quid Consumer Lending Platform"
   Deposito SIAE n. 012702 del 26 Luglio 2018
LEGAL-NOTICE */
import { SecurityService } from '@services/security/security.service';
import { mergeMap, map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Observable, of, forkJoin } from 'rxjs';
import { UserDataDTO } from '@api/models';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  /**
   * sessione utente
   */
  private session: UserDataDTO;

  /**
   * lista di azioni che vengono eseguite dopo la logon
   */
  private afterLogonActions: Observable<void>[] = [];

  constructor(private securityService: SecurityService) {}

  /**
   * effettua login tramite auth code
   */
  loginWithAuthCode(code: string): Observable<boolean> {
    return this.verifyAuthCode(code).pipe(mergeMap(() => this.startSession()));
  }

  /**
   * avvia sessione recuperando i dati dell'utente
   */
  startSession(): Observable<boolean> {
    return this.getUserData().pipe(
      map(res => this.setSession(res)),
      mergeMap(() => this.runAfterLogonActions()),
      map(() => true)
    );
  }

  /**
   * aggiunge azione da eseguire dopo il logon
   */
  addActionAfterLogon(action: Observable<void>): void {
    if (action instanceof Observable) {
      this.afterLogonActions.push(action);
    }
  }

  /**
   * restituisce la sessione attuale
   */
  getSession(): UserDataDTO {
    return (this.session && { ...this.session }) || {};
  }

  /**
   * restituisce l'id della position definito dalla session
   */
  getSessionPositionId(): string {
    const session = this.getSession();
    return session && session.attributes && session.attributes.positionId;
  }

  /**
   * restituisce il wizard code definito dalla session
   */
  getSessionWizardCode(): string {
    const session = this.getSession();
    return session && session.attributes && session.attributes.operation;
  }

  /**
   * determina se la sessione è impostata correttamente
   */
  isSessionSet(): boolean {
    return !!this.isSessionValid(this.session);
  }

  /**
   * verifica la validità dell'auth code
   */
  private verifyAuthCode(code: string): Observable<boolean> {
    return this.securityService.authenticate(code).pipe(map(() => true));
  }

  /**
   * restituisce i dati dell'user autenticato
   */
  private getUserData(): Observable<UserDataDTO> {
    return this.securityService.getUserData();
  }

  /**
   * imposta i dati della sessione e controlla che sia valida
   */
  private setSession(session: UserDataDTO): void {
    if (!this.isSessionValid(session)) {
      throw new Error(`User's session is not valid`);
    }

    this.session = (!!session && { ...session }) || null;
  }

  /**
   * controlla se la sessione è valida
   */
  private isSessionValid(session: UserDataDTO): boolean {
    return !!session;
  }

  /**
   * esegue le operazioni registrate da effettuare dopo il logon
   */
  private runAfterLogonActions(): Observable<boolean> {
    if (this.afterLogonActions.length === 0) {
      return of(true);
    }

    return forkJoin(this.afterLogonActions).pipe(map(() => true));
  }
}
